import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Headphones, 
  FileText, 
  CreditCard, 
  FolderOpen,
  ArrowRight,
  CheckCircle,
  ClipboardList,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { adminService } from '../../services/adminService';
import { ticketService } from '../../services/ticketService';
import { propostaService } from '../../services/propostaService';

interface DashboardStats {
  ticketsAbertos: number;
  propostasAbertas: number;
  faturasPendentes: number;
}

const serviceCards = [
  // Linha 1
  {
    icon: Headphones,
    title: 'Suporte Técnico',
    description: 'Abra tickets e acompanhe suas solicitações em tempo real',
    path: '/portal/tickets',
    color: 'bg-blue-500/10 border-blue-500/20 text-blue-600',
    iconColor: 'text-blue-600',
    status: 'Disponível'
  },
  {
    icon: ClipboardList,
    title: 'Meus Briefings',
    description: 'Preencha briefings de novos projetos solicitados pela nossa equipe',
    path: '/portal/briefings',
    color: 'bg-purple-500/10 border-purple-500/20 text-purple-600',
    iconColor: 'text-purple-600',
    status: 'Disponível'
  },
  // Linha 2
  {
    icon: FileText,
    title: 'Propostas',
    description: 'Visualize e gerencie suas propostas comerciais',
    path: '/portal/propostas',
    color: 'bg-green-500/10 border-green-500/20 text-green-600',
    iconColor: 'text-green-600',
    status: 'Disponível'
  },
  {
    icon: CreditCard,
    title: 'Faturas',
    description: 'Consulte e pague suas faturas pendentes',
    path: '/portal/faturas',
    color: 'bg-purple-500/10 border-purple-500/20 text-purple-600',
    iconColor: 'text-purple-600',
    status: 'Disponível'
  },
  {
    icon: BookOpen,
    title: 'Contratos',
    description: 'Acesse seus contratos e documentos',
    path: '/portal/contratos',
    color: 'bg-orange-500/10 border-orange-500/20 text-orange-600',
    iconColor: 'text-orange-600',
    status: 'Disponível'
  },
  {
    icon: FolderOpen,
    title: 'Notas Fiscais',
    description: 'Acesse suas notas fiscais e documentos',
    path: '/portal/documentos',
    color: 'bg-teal-500/10 border-teal-500/20 text-teal-600',
    iconColor: 'text-teal-600',
    status: 'Disponível'
  }
];

export function Portal() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    ticketsAbertos: 0,
    propostasAbertas: 0,
    faturasPendentes: 0
  });
  const [loading, setLoading] = useState(true);
  const [suporteInicio, setSuporteInicio] = useState<string>('');
  const [suporteFim, setSuporteFim] = useState<string>('');

  useEffect(() => {
    const loadStats = async () => {
      try {
        // Dados do cliente logado (RLS aplicado por cada endpoint)
        const [faturaStats, ticketsAbertos, propostasResult] = await Promise.all([
          adminService.getFaturaStats(),
          ticketService.list({ status: 'ABERTO' }),
          propostaService.list({ status: 'enviada', limit: 100 }),
        ]);

        setStats({
          ticketsAbertos: ticketsAbertos.length,
          propostasAbertas: propostasResult.data?.length ?? 0,
          faturasPendentes: faturaStats.pendentes,
        });
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  useEffect(() => {
    const loadSuporte = async () => {
      try {
        const response = await api.get('/theme');
        setSuporteInicio(response.data?.suporteInicio || '');
        setSuporteFim(response.data?.suporteFim || '');
      } catch (error) {
        console.error('Erro ao carregar horário de suporte:', error);
      }
    };

    loadSuporte();
  }, []);

  // Suporte está ativo quando não há horário configurado (24h) ou quando
  // o horário local atual está dentro do intervalo configurado.
  const suporteAtivo = (() => {
    if (!suporteInicio || !suporteFim) return true;
    const [hi, mi] = suporteInicio.split(':').map(Number);
    const [hf, mf] = suporteFim.split(':').map(Number);
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const start = hi * 60 + mi;
    const end = hf * 60 + mf;
    if (start <= end) return nowMin >= start && nowMin <= end;
    return nowMin >= start || nowMin <= end;
  })();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const firstName = user?.name?.split(' ')[0] || 'Cliente';

  return (
    <div className="space-y-8 relative">
      {/* Glow Effects - Light Mode */}
      <div className="glow-light-blue w-96 h-96 top-32 left-10 animate-glow-pulse" />
      <div className="glow-light-cyan w-[400px] h-[400px] bottom-20 right-20 animate-glow-drift" />
      
      {/* Glow Effects - Dark Mode */}
      <div className="glow-dark-blue w-[500px] h-[500px] top-20 -left-20 animate-glow-pulse" />
      <div className="glow-dark-cyan w-96 h-96 bottom-40 right-10 animate-glow-drift" style={{ animationDelay: '4s' }} />
      
      {/* Welcome Section */}
      <div className="space-y-4 relative z-10">
        <h1 className="text-4xl font-bold text-foreground">
          {getGreeting()},{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
            {firstName}
          </span>
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl">
          Bem-vindo ao seu portal exclusivo. Aqui você tem acesso rápido a todos os serviços e informações importantes da sua conta.
        </p>
        
        {/* Status Indicators */}
        <div className="flex items-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-muted-foreground">Sistema Online</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${suporteAtivo ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <span className="text-sm text-muted-foreground">
              {suporteAtivo ? 'Suporte Ativo' : 'Suporte Indisponível'}
            </span>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="space-y-6 relative z-10">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Serviços Disponíveis</h2>
          <p className="text-sm text-muted-foreground">Acesso rápido às suas ferramentas</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {serviceCards.slice(0, 2).map((service, index) => {
            const Icon = service.icon;
            return (
              <Link
                key={index}
                to={service.path}
                className="group relative card border-border hover:border-accent/60 p-6 transition-all duration-300 hover:shadow-md"
              >
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between">
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${service.color}`}>
                      <CheckCircle className="w-3 h-3" />
                      {service.status}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon className={`w-6 h-6 ${service.iconColor}`} />
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {serviceCards.slice(2).map((service, index) => {
            const Icon = service.icon;
            return (
              <Link
                key={index}
                to={service.path}
                className="group relative card border-border hover:border-accent/60 p-6 transition-all duration-300 hover:shadow-md"
              >
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between">
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${service.color}`}>
                      <CheckCircle className="w-3 h-3" />
                      {service.status}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon className={`w-6 h-6 ${service.iconColor}`} />
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid md:grid-cols-3 gap-6 relative z-10">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Tickets Abertos</h3>
            <Headphones className="w-5 h-5 text-blue-600" />
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-foreground">
              {loading ? '-' : stats.ticketsAbertos}
            </div>
            <p className="text-sm text-muted-foreground">Solicitações em andamento</p>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Propostas Ativas</h3>
            <FileText className="w-5 h-5 text-green-600" />
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-foreground">
              {loading ? '-' : stats.propostasAbertas}
            </div>
            <p className="text-sm text-muted-foreground">Aguardando aprovação</p>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Faturas Pendentes</h3>
            <CreditCard className="w-5 h-5 text-purple-600" />
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-foreground">
              {loading ? '-' : stats.faturasPendentes}
            </div>
            <p className="text-sm text-muted-foreground">Aguardando pagamento</p>
          </div>
        </div>
      </div>
    </div>
  );
}